<template lang="pug">
.chat.w-full.fade-in(ref="elRef")
  .bg-paper.rounded-lg.shadow-xl.overflow-hidden.my-12(v-if="!audioDoc")
    .bg-primary-200.dark_bg-primary-900.py-3.text-center.text-xl.tracking-wide
      uc-editable(
        :editPath="`${editPath}.chatTitle`"
        :k="`${screen.id}.chatTitle`"
        :text="screen.chatTitle"
        v-if="screen.chatTitle"
      )

    ul.flex.flex-col.p-3.md_p-12
      li.msg(
        :class="msg.you ? 'dark msg--you' : 'msg--them'"
        v-for="(msg, i) in screen.messages"
      )
        .flex.items-start.py-4(:class="{ 'flex-row-reverse': msg.you }")
          .msg__avatar(
            :class="twMerge('w-10 h-10 rounded-full inline-flex items-center justify-center bg-primary-600 text-white text-2xl', msg.them && 'bg-secondary-600', screen.styles?.[msg.you ? 'you' : 'them'])"
          )
            uc-asset(
              :src="msg.you ? screen.icons?.you || 'mdi:bird' : screen.icons?.them || 'whh:rabbit'"
              :size="isSrcIcon(msg) ? 1.5 : undefined"
              :class="isSrcIcon(msg) ? 'text-white' : undefined"
            )
          .msg__text
            //- .dots(v-if="msg.them")
            //-   uc-icon.dot.mr-1.text-gray-300(icon="mdi:circle")
            //-   uc-icon.dot.mr-1.text-gray-300(icon="mdi:circle")
            //-   uc-icon.dot.mr-1.text-gray-300(icon="mdi:circle")
            .msg__content.prose-stack
              //- expose for translations
              //- text="You: "
              //- text="Them: "
              uc-text(
                :text="msg.you ? 'You: ' : 'Them: '"
                uc-word-only=""
                uc-dont-read=""
                uc-word-italic=""
                uc-word-bold=""
              )
              uc-editable(
                :editPath="`${editPath}.messages.${i}.${msg.you ? 'you' : 'them'}`"
                :k="msg.you ? `${msg.id}.you` : `${msg.id}.them`"
                :text="msg.you || msg.them"
              )

  uc-editable.feedback.mb-8.fade-in(
    :editPath="`${editPath}.feedback`"
    :k="`${screen.id}.feedback`"
    :text="screen.feedback"
    v-if="screen.feedback"
  )
</template>

<script lang="ts" setup>
import gsap from 'gsap/all';
import { twMerge } from '../lib/mergeStyles';
import {
  onMounted,
  ref,
  unref,
  inject,
  watchEffect,
  toRef,
  type Ref
} from 'vue';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { setCompleted } from '../lib/SCORM';
import useSuspend from '../lib/useSuspend';
import useScreenAudio from '../lib/useScreenAudio';
import chatSchema from '../lib/screen-schemas/screen-chat.schema';

gsap.registerPlugin(ScrollTrigger);

const props = defineProps<{ editPath: string; screen: ScreenChat }>();

const elRef = ref(null);
const sectionId = inject('sectionId') as string;
const audioDoc = inject('audioDoc') as boolean;
const wordDoc = inject('wordDoc') as Ref<boolean>;
const isEditModeActive = inject('isEditModeActive') as Ref<boolean>;
const suspend = useSuspend();
const { queueSlot } = useScreenAudio(toRef(props, 'screen'), chatSchema);
let tl;
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

const breakpoints = useBreakpoints(breakpointsTailwind);
const smallDevice = breakpoints.smallerOrEqual('sm');

onMounted(() => {
  if (audioDoc || wordDoc.value) return;

  const el = unref(elRef);

  tl = gsap
    .timeline({
      paused: true,
      immediateRender: true,
      onComplete: () => {
        setCompleted(sectionId);
        if (props.screen.feedback) queueSlot('feedback');
      }
    })
    .set(el.querySelector('.feedback'), { opacity: 0 });

  el.querySelectorAll('.msg').forEach((msg: HTMLElement) => {
    const isThem = msg.closest('.msg--them');

    const contentTl = gsap.timeline();

    const msgTl = gsap
      .timeline()
      .fromTo(
        msg,
        { height: 0 },
        {
          height: 'auto',
          duration: 0.2
        }
      )
      .add(contentTl, '<0.1')
      .fromTo(
        msg,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.5,
          ease: 'linear'
        }
      )
      .fromTo(
        msg,
        isThem ? { y: -100 } : { y: 100 },
        { y: 0, duration: 0.5, ease: 'elastic.out(1.5,0.75)' },
        '<'
      )
      .fromTo(msg, { scaleX: 0.5 }, { scaleX: 1, duration: 0.2 }, '<');

    tl.add(msgTl, '>0.2');
  });

  tl.to(el.querySelector('.feedback'), { opacity: 1, duration: 1 });

  ScrollTrigger.create({
    trigger: el.closest('.section'),
    scroller: el.closest('.scroller'),
    start: ({ trigger }) => {
      return (trigger as HTMLElement).offsetHeight > window.innerHeight * 0.3
        ? // it's tall
          'top 80%'
        : // it's short
          'bottom 105%';
    },
    onEnter(self) {
      if (!el.offsetHeight) return;
      if (!self.isActive) return;

      if (smallDevice.value && suspend.completed[sectionId]) {
        tl.progress(1);
        return;
      }

      if (suspend.reducedMotion || isEditModeActive.value) {
        tl.progress(1);
      } else {
        tl.play(0);
      }
    },
    onEnterBack: self => {
      if (!el.offsetHeight) return;
      if (!self.isActive) return;

      // as we scroll back up, we need to complete the chat timeline, only if the section has been completed
      if (suspend.completed[sectionId]) tl.progress(1);
    },
    onLeaveBack() {
      tl.pause();
    }
  });
});

function isSrcIcon(msg: any) {
  const src = msg.you
    ? props.screen.icons?.you || 'mdi:bird'
    : props.screen.icons?.them || 'whh:rabbit';
  return typeof src === 'string' && /\w+:\w+/.test(src);
}

// reset the animation if the page is reset. Not doing this soft locks the user for quiz reset.
watchEffect(() => {
  if (!suspend.completed[props.screen.id]) tl && tl.progress(0).pause();
});
</script>

<style scoped>
.msg__text {
  @apply dark_bg-gray-900 clear-both mx-4 inline-block rounded-lg bg-gray-100 p-1 px-3 text-gray-800;
}

.msg.msg--you {
  .msg__text {
    @apply bg-primary-500;
  }

  .prose-stack {
    @apply text-white;
  }
}
</style>
